import { useRef, useState } from 'react'
import type { Block, BlockCreateInput } from '@/types/blocks'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { defaultBlockBody } from '@/features/editor/lib/blockDefaults'
import { readLessonLook } from '@/features/editor/lib/lessonLook'
import { toBlockCreate } from '@/features/editor/lib/duplicateBlock'
import { regenerateElementIds } from '@/features/editor/lib/designElements'
import type { ActiveBlockCard } from '@/features/editor/constants/blockTypeMatrix'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import type { BlocksInRun } from './useBlocksInRun'
import { useBlockReorder } from './useBlockReorder'
import { useOptimisticBlockCreate } from './useOptimisticBlockCreate'
import type { NewBlockPreview } from '@/features/editor/lib/newBlockPreview'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

interface UseLessonBlocksActionsInput {
  lessonId: number
  /**
   * Живая идентичность урока (`originId ?? id`). Пока список блоков урока под
   * НОВЫМ id (черновик завёлся, слит или отменён) ещё не приехал, лента
   * показывает прежние блоки того же урока, а не пустоту. Опционален ради
   * старых тестов хука.
   */
  lessonIdentity?: number
  /**
   * §23: прокидывается в `createBlock` ради инвалидации тега `Course` —
   * бейдж плашки урока (`lesson.blocksCount`) приезжает с деталью курса.
   * Обязателен: опциональность держала бы этот инвариант на дисциплине
   * вызывающего, а не на типах.
   */
  courseId: number
  onBlockCreated: (blockId: number) => void
  /** Блоки, занятые прогоном (`useBlocksInRun`). */
  blocksInRun: BlocksInRun
  /**
   * Task 10 (Б): любой прогон, пишущий в блоки, активен (`useIsBlockWritingRunActive`).
   * Гейт грубый — у картиночного прогона цели знает только сервер, точечный
   * гейт по `blocksInRun` для него невозможен. Опционален ради старых тестов
   * этого хука, которые прогон не эмулируют.
   */
  isBlockWritingRunActive?: boolean
  /**
   * Task 10: тема урока (`lesson.theme`) — источник у `readLessonLook`, тот
   * же объект урока, из которого вызывающий уже берёт `lessonId`. Опционален
   * ради старых тестов; без неё `readLessonLook` фолбэчится на якорь
   * bgColor уже загруженных блоков урока (легаси-путь).
   */
  lessonTheme?: string | null
  /**
   * Курс на проверке (`useIsCourseFrozen`, считает `useCourseTimeline`): состав
   * урока — часть заявки. Кнопки гаснут по тому же флагу (`BlockStrip`), а
   * хендлеры отказывают сами — кнопку могли нажать за миг до того, как пришёл
   * `pending`. Опционален ради старых
   * тестов этого хука, как `isBlockWritingRunActive`.
   */
  isFrozen?: boolean
}

export const useLessonBlocksActions = ({
  lessonId,
  lessonIdentity,
  courseId,
  onBlockCreated,
  blocksInRun,
  isBlockWritingRunActive = false,
  lessonTheme = null,
  isFrozen = false,
}: UseLessonBlocksActionsInput) => {
  const query = useGetLessonBlocksQuery(lessonId)
  // Последний приехавший список и чей он: при смене источника (новые id урока)
  // показываем его, пока не придёт новый, — иначе блоки на секунду пропадают.
  const shown = useRef<{ identity: number | undefined; blocks: Block[] } | null>(null)
  if (query.data) shown.current = { identity: lessonIdentity, blocks: query.data }
  const carried =
    !query.data && lessonIdentity !== undefined && shown.current?.identity === lessonIdentity
      ? shown.current.blocks
      : null
  const blocks = query.data ?? carried ?? []
  const isLoading = carried ? false : query.isLoading
  const look = readLessonLook(lessonTheme, blocks)
  const { handleReorder, isSourceSettling } = useBlockReorder({
    lessonId,
    courseId,
    blocks,
    blocksInRun,
    isBlockWritingRunActive,
    isFrozen,
  })
  const { createAt, isCreating } = useOptimisticBlockCreate({
    lessonId,
    courseId,
    onBlockCreated,
  })

  const [popoverOpen, setPopoverOpen] = useState(false)
  // Новый экран встаёт в конец, сразу выделен; сервер догоняет в фоне.
  // Блок статьи — тоже: он создаётся ПУСТЫМ (`articleId: null`, PRD #71, ф.3), а
  // статья выбирается потом на холсте или в панели — `PUT` этого же блока.
  // `article: null` — поле, которое у блока статьи сервер отдаёт всегда.
  const runCreate = (body: BlockCreateInput, errMsg: string) =>
    createAt({
      body,
      placeholder: {
        ...body,
        ...(body.type === 'article' ? { article: null, available: true } : {}),
        id: 0,
        order: 0,
        createdAt: '',
        updatedAt: '',
      } as Block,
      fallbackId: blocks.at(-1)?.id ?? null,
      errMsg,
    })

  const handleAdd = async (card: ActiveBlockCard) => {
    setPopoverOpen(false)
    if (refusedByReview(isFrozen)) return
    await runCreate(defaultBlockBody(card, look), messagesNow(messages).createBlockFailed)
  }

  /**
   * Копия ВЫБРАННОГО экрана встаёт В КОНЕЦ урока — туда же, куда её кладёт сервер
   * (решение заказчика, 12.10). Перенос за оригиналом убран: у живого курса он
   * второй пакетной записью расходился с черновиком и после перезагрузки копия
   * всё равно оказывалась в конце. Переставить её автор может перетаскиванием.
   */
  const handleDuplicate = async (blockId: number) => {
    const index = blocks.findIndex((b) => b.id === blockId)
    if (index === -1 || refusedByReview(isFrozen)) return
    const source = blocks[index]
    // id элементов дизайн-блока не шэрим между блоками — иначе копия и оригинал
    // ссылаются на одни и те же id (бек уникальность не проверяет, путаться будем мы).
    const body =
      source.type === 'design'
        ? { ...toBlockCreate(source), elements: regenerateElementIds(source.elements) }
        : toBlockCreate(source)
    // Копия видна и выделена сразу, в конце урока. Пустой видео-блок
    // копируется без подтверждения: правки живого курса уходят в черновик и до
    // «Обновить» к ученикам не попадают (решение Пети, ecd84cfe).
    await createAt({
      body,
      placeholder: source,
      fallbackId: source.id,
      errMsg: messagesNow(messages).duplicateBlockFailed,
    })
  }

  // Как будет выглядеть новый блок типа `card` — для перетекания примера в окне
  // выбора типа в настоящий блок (`AddBlockPicker`). У статьи пустой экран — это
  // выбор статьи со списком из сети, и примером он не рисуется: перетекания нет.
  const previewFor = (card: ActiveBlockCard): NewBlockPreview | null =>
    card.id === 'article'
      ? null
      : {
          block: {
            ...defaultBlockBody(card, look),
            id: 0,
            order: 0,
            createdAt: '',
            updatedAt: '',
          } as Block,
          // Новый блок встаёт в конец урока — так же считает полосу холст.
          total: blocks.length + 1,
          current: blocks.length,
        }

  return {
    blocks,
    previewFor,
    isLoading,
    isCreating,
    isSourceSettling,
    popoverOpen,
    setPopoverOpen,
    handleAdd,
    handleDuplicate,
    handleReorder,
  }
}
